import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DataTable from "../../../assets/components/dataTable";
import { Button } from "semantic-ui-react";
import { format } from "date-fns";
import MemberSearchSelect from "../../../assets/components/MemberSearchSelect";
import LoadingPlaceholder from "../../../assets/components/LoadingPlaceholder";
import MemberChip from "../../../assets/components/MemberChip";

const Usage = ({ contract }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [table, setTable] = useState({
        page: 1,
        term: "",
        sort: "createdAt",
        order: "desc",
        limit: 1000,
    });
    const [filters, setFilters] = useState({
        member: null,
    });

    const fetchData = async (params) => {
        const { data } = await contract.usage(params);
        setData(data);
        setLoading(false);
    };

    useEffect(() => {
        fetchData(table);
    }, []);

    const onChangeFilter = (property, value) => {
        filters[property] = value;
        table.page = 1;
        setFilters(filters);
        setTable(table);
        fetchData(Object.assign({}, table, filters));
    };

    const headers = [
        { key: "type", text: "Type" },
        { key: "user", text: "Member" },
        { key: "voucherBalls", text: "Voucher balls" },
        { key: "bookingBalls", text: "Booking balls" },
        { key: "course", text: "Course" },
        { key: "createdAt", text: "Date Created" },
        { key: "usedAt", text: "Booking Date" },
        { key: "booking", text: "Booking" },
        { key: "voucher", text: "Voucher" },
    ];
    const getUsageTypeLabel = (item) => {
        if (item.type === "VOUCHER") return "VOUCHER";
        if (item.type === "TEETIME" && item.voucher)
            return "TEETIME (VOUCHER USED)";
        return "TEETIME";
    };
    if (loading === true) {
        return <LoadingPlaceholder />;
    }

    return (
        <>
            <div className='row mt-5'>
                <div className='col'>
                    {contract.used !== data.length && (
                        <p>
                            <b>
                                NOTE: The "Balls used" used on the contract does
                                not equal the number of records shown below.
                                This is likely because of ad-hoc usage by MASHIE
                                staff against events bookings.
                            </b>
                        </p>
                    )}
                    <MemberSearchSelect
                        placeholder='Filter by member'
                        value={filters.member}
                        onChange={(value) => onChangeFilter("member", value)}
                    />
                </div>
            </div>
            <div className='row mt-3'>
                <div className='col'>
                    <h3>Uses</h3>
                    <DataTable
                        loading={loading}
                        headers={headers}
                        data={data.map((item) => {
                            const hasVoucher =
                                item.type === "VOUCHER" || !!item.voucher;

                            const voucherPlayers =
                                item.type === "VOUCHER"
                                    ? item.players
                                    : item.voucher?.players ?? null;

                            const bookingPlayers =
                                item.type === "TEETIME" ? item.players : null;

                            const voucherId =
                                item.voucher?._id ??
                                item.voucher ??
                                (item.type === "VOUCHER" ? item._id : null);

                            return {
                                type: { value: getUsageTypeLabel(item) },
                                user: {
                                    value: (
                                        <MemberChip
                                            member={item.user}
                                            hideMembership={true}
                                        />
                                    ),
                                },

                                voucherBalls: {
                                    value: (
                                        <span
                                            style={{
                                                fontWeight: hasVoucher
                                                    ? 700
                                                    : 400,
                                            }}
                                        >
                                            {voucherPlayers ?? "—"}
                                        </span>
                                    ),
                                },

                                bookingBalls: {
                                    value: (
                                        <span
                                            style={{
                                                fontWeight: hasVoucher
                                                    ? 400
                                                    : 700,
                                            }}
                                        >
                                            {bookingPlayers ?? "—"}
                                        </span>
                                    ),
                                },

                                course: {
                                    value: (
                                        <Link
                                            to={`/golf-clubs/${item.course.club._id}`}
                                        >
                                            {item.course.longName}
                                        </Link>
                                    ),
                                },

                                createdAt: {
                                    value: format(
                                        new Date(item.createdAt),
                                        "dd/MM/yyyy",
                                    ),
                                },

                                usedAt: {
                                    value: item.usedAt
                                        ? format(
                                              new Date(item.usedAt),
                                              "dd/MM/yyyy",
                                          )
                                        : "N/A",
                                },

                                booking: {
                                    value: (
                                        <>
                                            {item.type === "TEETIME" && (
                                                <Link
                                                    to={
                                                        "/tee-time-requests/" +
                                                        item._id
                                                    }
                                                >
                                                    <Button
                                                        primary
                                                        className='theme'
                                                    >
                                                        View Booking
                                                    </Button>
                                                </Link>
                                            )}
                                        </>
                                    ),
                                },

                                voucher: {
                                    value: (
                                        <>
                                            {voucherId && (
                                                <Link
                                                    to={
                                                        "/course-vouchers/" +
                                                        voucherId
                                                    }
                                                >
                                                    <Button
                                                        primary
                                                        className='theme'
                                                    >
                                                        View Voucher
                                                    </Button>
                                                </Link>
                                            )}
                                        </>
                                    ),
                                },
                            };
                        })}
                        totalRecords={data.length}
                        onChange={fetchData}
                        sort={table.sort}
                        hideSearch
                        order={table.order}
                        limit={table.limit}
                        sortOptions={[
                            { text: "Date created", key: "createdAt" },
                        ]}
                    />
                </div>
            </div>
            <div className='row mt-3'>
                <div className='col'>
                    <h3>Adjustments</h3>
                    <DataTable
                        loading={loading}
                        headers={[
                            { key: "adjustment", text: "Adjustment" },
                            { key: "reason", text: "Reason" },
                            { key: "createdBy", text: "Created by" },
                            { key: "createdAt", text: "Date created" },
                        ]}
                        data={contract.adjustments.map((item) => {
                            return {
                                adjustment: {
                                    value: `${item.adjustment > 0 ? "+" : ""}${
                                        item.adjustment
                                    }`,
                                },
                                reason: {
                                    value: item.reason,
                                },
                                createdBy: {
                                    value: item.createdBy.name,
                                },
                                createdAt: {
                                    value: format(
                                        new Date(item.createdAt),
                                        "dd/MM/yyyy",
                                    ),
                                },
                            };
                        })}
                        totalRecords={contract.adjustments.length}
                        hideSearch
                    />
                </div>
            </div>
        </>
    );
};

export default Usage;
